import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Plus, Copy, Edit3, Save, Loader2, GripVertical, Trash2,
  ChevronDown, ChevronRight, Settings2, FileSpreadsheet, Search,
  AlertCircle, Layers, Wand2, Calculator, Link, GitBranch, Scissors, Receipt, Lock, Unlock, ShieldCheck, Calendar, Hash,
  Maximize2, Minimize2, EyeOff, Eye, Power,
} from 'lucide-react';






import type {
  ExportTemplate,
  ExportTemplateColumn,
  TemplateColumnSourceType,
  TransformType,
  CalculatedConfig,
  CalcType,
  MarginRule,
  AccountScope,
  OtherTemplateRulesGroup,
} from './types';




import {
  CALC_TYPE_INFO as IMPORTED_CALC_TYPE_INFO,
  TARGET_SYSTEMS,
  FILE_TYPES,
  SOURCE_TYPE_INFO,
  SOURCE_FIELD_OPTIONS,
  SOURCE_FIELD_GROUPS,
  TRANSFORM_TYPE_INFO,
  DATE_FORMAT_OPTIONS,
} from './types';

import CalculatedColumnBuilder from './CalculatedColumnBuilder';
import MarginRulesManager from './MarginRulesManager';
import AccountScopePicker from './AccountScopePicker';
import ColumnEditorExpanded from './ColumnEditorExpanded';





// Local fallback for CALC_TYPE_INFO in case of bundler/import issues
const CALC_TYPE_INFO_FALLBACK: Record<string, { label: string; description: string; icon: string; color: string }> = {
  concat: {
    label: 'Concatenation',
    description: 'Build a value from multiple fields, text, and separators',
    icon: 'Link',
    color: 'bg-violet-900/40 text-violet-400 border-violet-700/50',
  },
  math: {
    label: 'Math',
    description: 'Simple numeric calculation from fields and values',
    icon: 'Calculator',
    color: 'bg-amber-900/40 text-amber-400 border-amber-700/50',
  },
  conditional_text: {
    label: 'Conditional Text',
    description: 'Return different text based on a field condition',
    icon: 'GitBranch',
    color: 'bg-rose-900/40 text-rose-400 border-rose-700/50',
  },
  split: {
    label: 'Split',
    description: 'Split a field value by a delimiter and keep a specific part',
    icon: 'Scissors',
    color: 'bg-sky-900/40 text-sky-400 border-sky-700/50',
  },
  gst: {
    label: 'GST',
    description: 'Calculate GST-inclusive, GST-exclusive, or GST amount from a price field',
    icon: 'Receipt',
    color: 'bg-emerald-900/40 text-emerald-400 border-emerald-700/50',
  },

    excel_formula: {
    label: 'Excel Formula',
    description: 'Write a live Excel formula for XLSX exports using {{Column Name}} placeholders',
    icon: 'FileSpreadsheet',
    color: 'bg-cyan-900/40 text-cyan-400 border-cyan-700/50',
  },
};



// Use imported value if available, otherwise fall back to local definition
const CALC_TYPE_INFO = (typeof IMPORTED_CALC_TYPE_INFO !== 'undefined' && IMPORTED_CALC_TYPE_INFO) 
  ? IMPORTED_CALC_TYPE_INFO 
  : CALC_TYPE_INFO_FALLBACK;




// ─── Template Builder Component ─────────────────────────────────────

interface TemplateBuilderProps {
  selectedTemplateId: string | null;
  onTemplateChange: (templateId: string | null) => void;
  onColumnsChange?: (columns: ExportTemplateColumn[]) => void;
  onMarginRulesChange?: (rules: MarginRule[]) => void;
  accountScope?: AccountScope | null;
  onAccountScopeChange?: (scope: AccountScope | null) => void;
  seriesFilter?: string[];
  /** Export's Date To filter (YYYY-MM-DD), used to preview date columns in "Last Day of Export" mode */
  rangeEndDate?: string;
}


const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  selectedTemplateId,
  onTemplateChange,
  onColumnsChange,
  onMarginRulesChange,
  accountScope,
  onAccountScopeChange,
  seriesFilter,
  rangeEndDate,
}) => {


  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [columns, setColumns] = useState<ExportTemplateColumn[]>([]);
  const [marginRules, setMarginRulesLocal] = useState<MarginRule[]>([]);
  const [otherTemplateRulesGroups, setOtherTemplateRulesGroups] = useState<OtherTemplateRulesGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [columnsLoading, setColumnsLoading] = useState(false);


  const [metaName, setMetaName] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [metaTarget, setMetaTarget] = useState('Generic');
  const [metaFileType, setMetaFileType] = useState('csv');

  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newTarget, setNewTarget] = useState('Generic');
  const [newFileType, setNewFileType] = useState('csv');

  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [dupName, setDupName] = useState('');
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameName, setRenameName] = useState('');

  const [expandedColId, setExpandedColId] = useState<string | null>(null);



  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const [metaCollapsed, setMetaCollapsed] = useState(false);
  const [columnsCollapsed, setColumnsCollapsed] = useState(false);
  const [columnsPopout, setColumnsPopout] = useState(false);


  // Notify parent of column changes
  const updateColumns = useCallback((newCols: ExportTemplateColumn[]) => {
    setColumns(newCols);
    onColumnsChange?.(newCols);
  }, [onColumnsChange]);

  // Handler for margin rules: update local state AND notify parent
  const handleMarginRulesChange = useCallback((rules: MarginRule[]) => {
    setMarginRulesLocal(rules);
    onMarginRulesChange?.(rules);
  }, [onMarginRulesChange]);


  // ─── Load Templates ─────────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('export_templates').select('*').order('template_name');
      if (error) throw error;
      setTemplates((data || []) as ExportTemplate[]);
    } catch (err: any) {
      toast({ title: 'Error', description: 'Failed to load templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ─── Load Columns ───────────────────────────────────────────────
  const loadColumns = useCallback(async (templateId: string) => {
    setColumnsLoading(true);
    try {
      const { data, error } = await supabase
        .from('export_template_columns').select('*')
        .eq('template_id', templateId).order('column_order');
      if (error) throw error;
      const cols = (data || []) as ExportTemplateColumn[];
      setColumns(cols);
      onColumnsChange?.(cols);
    } catch (err: any) {
      toast({ title: 'Error', description: 'Failed to load template columns', variant: 'destructive' });
    } finally {
      setColumnsLoading(false);
    }
  }, [onColumnsChange]);

  useEffect(() => {
    if (selectedTemplateId) {
      loadColumns(selectedTemplateId);
      const tmpl = templates.find(t => t.id === selectedTemplateId);
      if (tmpl) {
        setMetaName(tmpl.template_name);
        setMetaDesc(tmpl.description || '');
        setMetaTarget(tmpl.target_system);
        setMetaFileType(tmpl.default_file_type);
      }
    } else {
      updateColumns([]);
    }
  }, [selectedTemplateId, templates, loadColumns, updateColumns]);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId) || null;
  const isLocked = selectedTemplate?.is_locked ?? false;

  // ─── Official / Locked Handlers (Part 9) ────────────────────────
  const handleToggleOfficial = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('export_templates')
        .update({ is_official: !selectedTemplate.is_official, updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', selectedTemplate.id);
      if (error) throw error;
      toast({ title: selectedTemplate.is_official ? 'Unmarked' : 'Marked Official', description: `Template "${selectedTemplate.template_name}" ${selectedTemplate.is_official ? 'is no longer official' : 'marked as official'}` });
      await loadTemplates();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleToggleLocked = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('export_templates')
        .update({ is_locked: !selectedTemplate.is_locked, updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', selectedTemplate.id);
      if (error) throw error;
      toast({ title: selectedTemplate.is_locked ? 'Unlocked' : 'Locked', description: `Template "${selectedTemplate.template_name}" ${selectedTemplate.is_locked ? 'unlocked for editing' : 'locked from editing'}` });
      await loadTemplates();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update', variant: 'destructive' });
    } finally { setSaving(false); }
  };


  // ─── Template CRUD ──────────────────────────────────────────────
  const handleCreateTemplate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.from('export_templates')
        .insert({ template_name: newName.trim(), description: newDesc.trim() || null, target_system: newTarget, default_file_type: newFileType, is_active: true, created_by: 'admin' })
        .select().single();
      if (error) throw error;
      toast({ title: 'Created', description: `Template "${newName}" created` });
      setShowNewDialog(false); setNewName(''); setNewDesc(''); setNewTarget('Generic'); setNewFileType('csv');
      await loadTemplates();
      onTemplateChange(data.id);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to create template', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDuplicate = async () => {
    if (!selectedTemplate || !dupName.trim()) return;
    setSaving(true);
    try {
      const { data: newTmpl, error: tmplErr } = await supabase.from('export_templates')
        .insert({ template_name: dupName.trim(), description: selectedTemplate.description, target_system: selectedTemplate.target_system, default_file_type: selectedTemplate.default_file_type, is_active: true, created_by: 'admin' })
        .select().single();
      if (tmplErr) throw tmplErr;
      if (columns.length > 0) {
        const newCols = columns.map(c => ({
          template_id: newTmpl.id, column_order: c.column_order, export_column_name: c.export_column_name,
          source_type: c.source_type, source_field: c.source_field, static_value: c.static_value,
          default_value: c.default_value, is_required: c.is_required, is_enabled: c.is_enabled,
          notes: c.notes, transform_type: c.transform_type, transform_config: c.transform_config,
        }));
        const { error: colErr } = await supabase.from('export_template_columns').insert(newCols);
        if (colErr) throw colErr;
      }
      toast({ title: 'Duplicated', description: `Template duplicated as "${dupName}"` });
      setShowDuplicateDialog(false); setDupName('');
      await loadTemplates();
      onTemplateChange(newTmpl.id);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to duplicate', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleRename = async () => {
    if (!selectedTemplate || !renameName.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('export_templates')
        .update({ template_name: renameName.trim(), updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', selectedTemplate.id);
      if (error) throw error;
      toast({ title: 'Renamed', description: `Template renamed to "${renameName}"` });
      setShowRenameDialog(false); setRenameName('');
      await loadTemplates();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to rename', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleArchive = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('export_templates')
        .update({ is_active: !selectedTemplate.is_active, updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', selectedTemplate.id);
      if (error) throw error;
      toast({ title: selectedTemplate.is_active ? 'Deactivated' : 'Activated', description: `Template "${selectedTemplate.template_name}" ${selectedTemplate.is_active ? 'set to inactive' : 'set to active'}` });
      // If we just deactivated and inactive templates are hidden, auto-enable showing them so the user can still see the template
      if (selectedTemplate.is_active && !showInactive) {
        setShowInactive(true);
      }
      await loadTemplates();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update template status', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleSaveMeta = async () => {
    if (!selectedTemplate || !metaName.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('export_templates')
        .update({ template_name: metaName.trim(), description: metaDesc.trim() || null, target_system: metaTarget, default_file_type: metaFileType, updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', selectedTemplate.id);
      if (error) throw error;
      toast({ title: 'Saved', description: 'Template metadata updated' });
      await loadTemplates();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // ─── Column CRUD ────────────────────────────────────────────────
  const handleAddColumn = async () => {
    if (!selectedTemplateId) return;
    const nextOrder = columns.length > 0 ? Math.max(...columns.map(c => c.column_order)) + 1 : 1;
    setSaving(true);
    try {
      const { data, error } = await supabase.from('export_template_columns')
        .insert({ template_id: selectedTemplateId, column_order: nextOrder, export_column_name: `Column ${nextOrder}`, source_type: 'blank', is_required: false, is_enabled: true })
        .select().single();
      if (error) throw error;
      const newCols = [...columns, data as ExportTemplateColumn];
      updateColumns(newCols);
      setExpandedColId(data.id);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to add column', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleUpdateColumn = async (colId: string, updates: Partial<ExportTemplateColumn>) => {
    setSaving(true);
    try {
      const { error } = await supabase.from('export_template_columns')
        .update({ ...updates, updated_at: new Date().toISOString() }).eq('id', colId);
      if (error) throw error;
      const newCols = columns.map(c => c.id === colId ? { ...c, ...updates } : c);
      updateColumns(newCols);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update column', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDeleteColumn = async (colId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase.from('export_template_columns').delete().eq('id', colId);
      if (error) throw error;
      updateColumns(columns.filter(c => c.id !== colId));
      if (expandedColId === colId) setExpandedColId(null);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to remove column', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // ─── Drag Reorder ──────────────────────────────────────────────
  const handleDragStart = (idx: number) => { dragItem.current = idx; };
  const handleDragEnter = (idx: number) => { dragOverItem.current = idx; };
  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
      dragItem.current = null; dragOverItem.current = null; return;
    }
    const reordered = [...columns];
    const [dragged] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, dragged);
    const updated = reordered.map((c, i) => ({ ...c, column_order: i + 1 }));
    updateColumns(updated);
    dragItem.current = null; dragOverItem.current = null;
    try {
      await Promise.all(updated.map(c =>
        supabase.from('export_template_columns').update({ column_order: c.column_order, updated_at: new Date().toISOString() }).eq('id', c.id)
      ));
    } catch (err: any) {
      toast({ title: 'Error', description: 'Failed to save column order', variant: 'destructive' });
    }
  };

  // ─── Shared Column Editor Callbacks ─────────────────────────────
  const handleLocalChange = useCallback((colId: string, updates: Partial<ExportTemplateColumn>) => {
    setColumns(prev => prev.map(c => c.id === colId ? { ...c, ...updates } : c));
  }, []);

  // ─── Helpers ────────────────────────────────────────────────────
  const getFieldLabel = (fieldName: string | null) => {
    if (!fieldName) return null;
    return SOURCE_FIELD_OPTIONS.find(f => f.field === fieldName)?.label || fieldName;
  };


  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Template Selector */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary" />
              <CardTitle className="text-sm font-semibold">Export Templates</CardTitle>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowNewDialog(true)} className="h-7 text-xs border-border">
              <Plus className="w-3 h-3 mr-1" /> New
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">
          {/* Template dropdown — filters inactive unless toggle is on, always shows currently selected */}
          {(() => {
            const inactiveCount = templates.filter(t => !t.is_active).length;
            const visibleTemplates = templates.filter(t => t.is_active || showInactive || t.id === selectedTemplateId);
            return (
              <>
                <Select value={selectedTemplateId || '__none'} onValueChange={(v) => onTemplateChange(v === '__none' ? null : v)}>
                  <SelectTrigger className="dark-input text-xs h-8"><SelectValue placeholder="Select a template..." /></SelectTrigger>
                  <SelectContent>
                    {visibleTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="flex items-center gap-2">
                          <span className={!t.is_active ? 'text-muted-foreground' : ''}>{t.template_name}</span>
                          {!t.is_active && <Badge className="bg-gray-800/40 text-gray-500 text-[9px] px-1 py-0">Inactive</Badge>}
                          {t.is_official && <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[9px] px-1 py-0">Official</Badge>}
                          {t.is_locked && <Badge className="bg-rose-900/30 text-rose-400 border-rose-700/40 text-[9px] px-1 py-0">Locked</Badge>}
                          <Badge className="bg-secondary text-muted-foreground text-[9px] px-1 py-0">{t.target_system}</Badge>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Show inactive toggle — only displayed when there are inactive templates */}
                {inactiveCount > 0 && (
                  <div className="flex items-center justify-between mt-2 px-0.5">
                    <label
                      htmlFor="show-inactive-toggle"
                      className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer select-none"
                    >
                      {showInactive ? <Eye className="w-3 h-3 text-muted-foreground" /> : <EyeOff className="w-3 h-3 text-muted-foreground/50" />}
                      Show inactive templates
                      <Badge className="bg-gray-800/40 text-gray-500 text-[9px] px-1 py-0 ml-0.5">{inactiveCount}</Badge>
                    </label>
                    <Switch
                      id="show-inactive-toggle"
                      checked={showInactive}
                      onCheckedChange={setShowInactive}
                      className="scale-75 origin-right"
                    />
                  </div>
                )}
              </>
            );
          })()}

          {selectedTemplate && (
            <>
              {/* Status / Official / Locked badges */}
              <div className="flex items-center gap-1.5 mt-2">
                {!selectedTemplate.is_active && (
                  <Badge className="bg-gray-800/40 text-gray-500 border-gray-700/40 text-[9px] px-1.5 py-0">
                    <Power className="w-2.5 h-2.5 mr-0.5" /> Inactive
                  </Badge>
                )}
                {selectedTemplate.is_official && (
                  <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[9px] px-1.5 py-0">
                    <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> Official
                  </Badge>
                )}
                {selectedTemplate.is_locked && (
                  <Badge className="bg-rose-900/30 text-rose-400 border-rose-700/40 text-[9px] px-1.5 py-0">
                    <Lock className="w-2.5 h-2.5 mr-0.5" /> Locked
                  </Badge>
                )}
              </div>
              {/* Locked warning */}
              {isLocked && (
                <div className="mt-1.5 flex items-start gap-1.5 text-[9px] text-rose-400/80 bg-rose-900/10 border border-rose-700/20 rounded px-2 py-1.5">
                  <Lock className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>This template is locked. Duplicate it to create an editable copy.</span>
                </div>
              )}
              {/* Action buttons */}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {!isLocked && (
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                    onClick={() => { setRenameName(selectedTemplate.template_name); setShowRenameDialog(true); }}>
                    <Edit3 className="w-3 h-3 mr-1" /> Rename
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                  onClick={() => { setDupName(`${selectedTemplate.template_name} (Copy)`); setShowDuplicateDialog(true); }}>
                  <Copy className="w-3 h-3 mr-1" /> {isLocked ? 'Duplicate to Edit' : 'Duplicate'}
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                  onClick={handleToggleOfficial} disabled={saving}>
                  <ShieldCheck className="w-3 h-3 mr-1" /> {selectedTemplate.is_official ? 'Unmark Official' : 'Mark Official'}
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                  onClick={handleToggleLocked} disabled={saving}>
                  {selectedTemplate.is_locked ? <Unlock className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />}
                  {selectedTemplate.is_locked ? 'Unlock' : 'Lock'}
                </Button>
                {!isLocked && (
                  <Button size="sm" variant="ghost" className={`h-6 text-[10px] px-2 ${selectedTemplate.is_active ? 'text-muted-foreground hover:text-foreground' : 'text-green-400/70 hover:text-green-400'}`}
                    onClick={handleArchive} disabled={saving}>
                    <Power className="w-3 h-3 mr-1" /> {selectedTemplate.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                )}
              </div>
            </>

          )}
        </CardContent>
      </Card>


      {/* Template Metadata */}
      {selectedTemplate && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-3 px-4 cursor-pointer" onClick={() => setMetaCollapsed(!metaCollapsed)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold">Template Settings</CardTitle>
              </div>
              {metaCollapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </CardHeader>
          {!metaCollapsed && !isLocked && (

            <CardContent className="px-4 pb-3 pt-0 space-y-2">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase">Template Name</Label>
                <Input value={metaName} onChange={e => setMetaName(e.target.value)} className="dark-input text-xs h-8" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase">Description</Label>
                <Textarea value={metaDesc} onChange={e => setMetaDesc(e.target.value)} className="dark-input text-xs min-h-[50px] resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Target System</Label>
                  <Select value={metaTarget} onValueChange={setMetaTarget}>
                    <SelectTrigger className="dark-input text-xs h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{TARGET_SYSTEMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">File Type</Label>
                  <Select value={metaFileType} onValueChange={setMetaFileType}>
                    <SelectTrigger className="dark-input text-xs h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{FILE_TYPES.map(f => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button size="sm" onClick={handleSaveMeta} disabled={saving} className="w-full golden-button h-7 text-xs">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Save Settings
              </Button>
            </CardContent>
          )}
          {!metaCollapsed && isLocked && (
            <CardContent className="px-4 pb-3 pt-0">
              <div className="text-[10px] text-muted-foreground/60 space-y-1">
                <div><span className="text-muted-foreground">Name:</span> <span className="text-foreground">{selectedTemplate?.template_name}</span></div>
                <div><span className="text-muted-foreground">Target:</span> <span className="text-foreground">{selectedTemplate?.target_system}</span></div>
                <div><span className="text-muted-foreground">File Type:</span> <span className="text-foreground">{selectedTemplate?.default_file_type?.toUpperCase()}</span></div>
                {selectedTemplate?.description && <div><span className="text-muted-foreground">Description:</span> <span className="text-foreground">{selectedTemplate.description}</span></div>}
                <p className="text-rose-400/60 mt-1.5 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Settings are read-only on locked templates</p>
              </div>
            </CardContent>
          )}
        </Card>
      )}


      {selectedTemplate && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between">
              <div
                className="flex items-center gap-2 cursor-pointer select-none"
                onClick={() => setColumnsCollapsed(!columnsCollapsed)}
              >
                {columnsCollapsed ? (
                  <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform" />
                )}
                <FileSpreadsheet className="w-4 h-4 text-primary" />
                <CardTitle className="text-sm font-semibold">Export Columns</CardTitle>
                <Badge className="bg-secondary text-muted-foreground text-[10px] px-1.5 py-0">{columns.length}</Badge>
              </div>
              <div className="flex items-center gap-1.5">
                {columns.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setColumnsPopout(true)}
                    className="h-7 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                    title="Pop out to modal for easier viewing"
                  >
                    <Maximize2 className="w-3 h-3 mr-1" />
                    Pop Out
                  </Button>
                )}
                {!isLocked ? (
                  <Button size="sm" variant="outline" onClick={handleAddColumn} disabled={saving} className="h-7 text-xs border-border">
                    <Plus className="w-3 h-3 mr-1" /> Add Column
                  </Button>
                ) : (
                  <Badge className="bg-rose-900/20 text-rose-400/60 border-rose-700/30 text-[9px] px-1.5 py-0">
                    <Lock className="w-2.5 h-2.5 mr-0.5" /> Read-Only
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>

          {!columnsCollapsed && (


          <CardContent className="px-4 pb-3 pt-0">
            {columnsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading columns...
              </div>
            ) : columns.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-xs">
                <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No columns defined yet.</p>
                <p className="text-[10px] mt-1">Click "Add Column" to start building your export.</p>
              </div>
            ) : (
              <div className="space-y-1 pr-1">
                {columns.map((col, idx) => {
                  const isExpanded = expandedColId === col.id;
                  const typeInfo = SOURCE_TYPE_INFO[col.source_type as TemplateColumnSourceType] || SOURCE_TYPE_INFO.blank;
                  const hasTransform = col.transform_type && col.transform_type !== 'none';
                  return (
                    <div
                      key={col.id}
                      className={`border rounded-md transition-colors ${isExpanded ? 'border-primary/40 bg-primary/5' : 'border-border/60 hover:border-border'} ${!col.is_enabled ? 'opacity-50' : ''}`}
                      draggable onDragStart={() => handleDragStart(idx)} onDragEnter={() => handleDragEnter(idx)}
                      onDragEnd={handleDragEnd} onDragOver={e => e.preventDefault()}
                    >
                      {/* Column Header Row */}
                      <div className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer" onClick={() => setExpandedColId(isExpanded ? null : col.id)}>
                        <GripVertical className="w-3 h-3 text-muted-foreground/40 cursor-grab flex-shrink-0" />
                        <span className="text-[10px] text-muted-foreground/60 w-5 text-center flex-shrink-0">{col.column_order}</span>
                        <span className="text-xs font-medium text-foreground truncate flex-1 min-w-0">{col.export_column_name}</span>
                        <Badge className={`${typeInfo.color} text-[9px] px-1 py-0 flex-shrink-0`}>{typeInfo.label}</Badge>
                        {col.source_type === 'calculated' && col.transform_config?.calcType && (
                          <Badge className={`${CALC_TYPE_INFO[col.transform_config.calcType as CalcType]?.color || 'bg-amber-900/40 text-amber-400'} text-[9px] px-1 py-0 flex-shrink-0`}>
                            {col.transform_config.calcType === 'concat' && <Link className="w-2.5 h-2.5 mr-0.5" />}
                            {col.transform_config.calcType === 'math' && <Calculator className="w-2.5 h-2.5 mr-0.5" />}
                            {col.transform_config.calcType === 'conditional_text' && <GitBranch className="w-2.5 h-2.5 mr-0.5" />}
                            {col.transform_config.calcType === 'split' && <Scissors className="w-2.5 h-2.5 mr-0.5" />}
                            {col.transform_config.calcType === 'gst' && <Receipt className="w-2.5 h-2.5 mr-0.5" />}
                          </Badge>
                        )}
                        {/* Blank column invoice number badge */}
                        {col.source_type === 'blank' && col.transform_config?.blankMode === 'invoice_number' && (
                          <span className="text-[9px] text-indigo-400 truncate max-w-[80px] flex items-center gap-0.5">
                            <Hash className="w-2.5 h-2.5" />
                            #{col.transform_config.startingInvoiceNumber || '?'}
                          </span>
                        )}

                        {hasTransform && col.source_type !== 'calculated' && (
                          <Badge className="bg-cyan-900/40 text-cyan-400 border-cyan-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                            <Wand2 className="w-2.5 h-2.5 mr-0.5" />{TRANSFORM_TYPE_INFO[col.transform_type as TransformType]?.label || col.transform_type}
                          </Badge>
                        )}
                        {col.source_type === 'source_field' && col.source_field && !hasTransform && (
                          <span className="text-[9px] text-muted-foreground truncate max-w-[70px]">{getFieldLabel(col.source_field)}</span>
                        )}
                        {col.source_type === 'static_value' && col.static_value && (
                          <span className="text-[9px] text-green-400 truncate max-w-[60px]">= {col.static_value}</span>
                        )}
                        {col.source_type === 'date' && col.transform_config?.dateMode && (
                          <span className="text-[9px] text-indigo-400 truncate max-w-[80px] flex items-center gap-0.5">
                            <Calendar className="w-2.5 h-2.5" />
                            {col.transform_config.dateMode === 'today' ? 'Today' : col.transform_config.customDate || 'Custom'}
                          </span>
                        )}
                        {col.is_required && <span className="text-[9px] text-red-400 flex-shrink-0">REQ</span>}
                        {col.transform_config?.excludeFromExport && <EyeOff className="w-3 h-3 text-orange-400 flex-shrink-0" title="Excluded from export" />}


                      </div>

                      {/* Expanded Column Editor — uses shared ColumnEditorExpanded */}
                      {isExpanded && (
                        <ColumnEditorExpanded
                          col={col}
                          columns={columns}
                          onLocalChange={handleLocalChange}
                          onPersistChange={handleUpdateColumn}
                          onDelete={handleDeleteColumn}
                          saving={saving}
                          isLocked={isLocked}
                          marginRules={marginRules}
                          otherTemplateRulesGroups={otherTemplateRulesGroups}
                          rangeEndDate={rangeEndDate}
                        />
                      )}


                    </div>
                  );
                })}
              </div>
            )}

            {/* Column Summary */}
            {columns.length > 0 && (
              <div className="mt-3 pt-2 border-t border-border/40 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                <span>{columns.filter(c => c.is_enabled).length} enabled</span>
                <span className="text-border">|</span>
                <span>{columns.filter(c => c.source_type === 'source_field').length} mapped</span>
                <span className="text-border">|</span>
                <span>{columns.filter(c => c.source_type === 'static_value').length} static</span>
                <span className="text-border">|</span>
                <span>{columns.filter(c => c.source_type === 'blank').length} blank</span>
                {columns.filter(c => c.transform_type && c.transform_type !== 'none').length > 0 && (
                  <>
                    <span className="text-border">|</span>
                    <span className="text-cyan-400">{columns.filter(c => c.transform_type && c.transform_type !== 'none').length} transformed</span>
                  </>
                )}
              </div>
            )}
          </CardContent>
          )}

          {/* Collapsed summary when columns are hidden */}
          {columnsCollapsed && columns.length > 0 && (
            <CardContent className="px-4 pb-2 pt-0">
              <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                <span>{columns.filter(c => c.is_enabled).length} enabled</span>
                <span className="text-border">|</span>
                <span>{columns.filter(c => c.source_type === 'source_field').length} mapped</span>
                <span className="text-border">|</span>
                <span>{columns.filter(c => c.source_type === 'static_value').length} static</span>
                <span className="text-border">|</span>
                <span>{columns.filter(c => c.source_type === 'blank').length} blank</span>
              </div>
            </CardContent>
          )}
        </Card>

      )}

      {/* ─── Margin Rules (Part 5) ─── */}
      {selectedTemplate && selectedTemplateId && (
        <MarginRulesManager
          templateId={selectedTemplateId}
          templateColumns={columns}
          onRulesChange={handleMarginRulesChange}
          onOtherTemplateRulesChange={setOtherTemplateRulesGroups}
        />
      )}


      {/* ─── Account Scope Picker ─── */}
      {selectedTemplate && selectedTemplateId && (
        <AccountScopePicker
          templateId={selectedTemplateId}
          accountScope={accountScope ?? selectedTemplate.account_scope}
          onScopeChange={(scope) => onAccountScopeChange?.(scope)}
          seriesFilter={seriesFilter}
          isLocked={isLocked}
        />
      )}






      {/* No Template Selected */}
      {!selectedTemplateId && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          <Layers className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select or create a template</p>
          <p className="text-xs mt-1 opacity-60">Templates define the export column structure</p>
        </div>
      )}

      {/* ─── Dialogs ─────────────────────────────────────────────── */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Create New Template</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Define a new export column template for a target system.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Template Name</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. MYOB Monthly Invoice" className="dark-input text-xs" /></div>
            <div><Label className="text-xs text-muted-foreground">Description</Label>
              <Textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional description..." className="dark-input text-xs min-h-[60px] resize-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Target System</Label>
                <Select value={newTarget} onValueChange={setNewTarget}><SelectTrigger className="dark-input text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{TARGET_SYSTEMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-muted-foreground">File Type</Label>
                <Select value={newFileType} onValueChange={setNewFileType}><SelectTrigger className="dark-input text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FILE_TYPES.map(f => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewDialog(false)} className="text-muted-foreground">Cancel</Button>
            <Button onClick={handleCreateTemplate} disabled={saving || !newName.trim()} className="golden-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Duplicate Template</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Create a copy of "{selectedTemplate?.template_name}" with all columns.</DialogDescription>
          </DialogHeader>
          <div><Label className="text-xs text-muted-foreground">New Template Name</Label>
            <Input value={dupName} onChange={e => setDupName(e.target.value)} className="dark-input text-xs" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDuplicateDialog(false)} className="text-muted-foreground">Cancel</Button>
            <Button onClick={handleDuplicate} disabled={saving || !dupName.trim()} className="golden-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Rename Template</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Enter a new name for this template.</DialogDescription>
          </DialogHeader>
          <div><Label className="text-xs text-muted-foreground">Template Name</Label>
            <Input value={renameName} onChange={e => setRenameName(e.target.value)} className="dark-input text-xs" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRenameDialog(false)} className="text-muted-foreground">Cancel</Button>
            <Button onClick={handleRename} disabled={saving || !renameName.trim()} className="golden-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Columns Pop-Out Modal ─────────────────────────────────── */}
      <Dialog open={columnsPopout} onOpenChange={setColumnsPopout}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                <DialogTitle className="text-foreground">
                  Export Columns
                  {selectedTemplate && <span className="text-muted-foreground font-normal ml-2">— {selectedTemplate.template_name}</span>}
                </DialogTitle>
                <Badge className="bg-secondary text-muted-foreground text-[10px] px-1.5 py-0">{columns.length}</Badge>
              </div>
              <div className="flex items-center gap-1.5">
                {!isLocked && (
                  <Button size="sm" variant="outline" onClick={handleAddColumn} disabled={saving} className="h-7 text-xs border-border">
                    <Plus className="w-3 h-3 mr-1" /> Add Column
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setColumnsPopout(false)} className="h-7 text-[10px] px-2 text-muted-foreground hover:text-foreground">
                  <Minimize2 className="w-3 h-3 mr-1" /> Collapse
                </Button>
              </div>
            </div>
            <DialogDescription className="text-muted-foreground text-xs">
              View and edit all {columns.length} export columns. Click a column to expand its settings.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1 space-y-1 min-h-0">
            {columns.map((col, idx) => {
              const isExpanded = expandedColId === col.id;
              const typeInfo = SOURCE_TYPE_INFO[col.source_type as TemplateColumnSourceType] || SOURCE_TYPE_INFO.blank;
              const hasTransform = col.transform_type && col.transform_type !== 'none';
              return (
                <div
                  key={col.id}
                  className={`border rounded-md transition-colors ${isExpanded ? 'border-primary/40 bg-primary/5' : 'border-border/60 hover:border-border'} ${!col.is_enabled ? 'opacity-50' : ''}`}
                  draggable onDragStart={() => handleDragStart(idx)} onDragEnter={() => handleDragEnter(idx)}
                  onDragEnd={handleDragEnd} onDragOver={e => e.preventDefault()}
                >
                  <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" onClick={() => setExpandedColId(isExpanded ? null : col.id)}>
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 cursor-grab flex-shrink-0" />
                    <span className="text-xs text-muted-foreground/60 w-6 text-center flex-shrink-0 font-mono">{col.column_order}</span>
                    <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">{col.export_column_name}</span>
                    <Badge className={`${typeInfo.color} text-[9px] px-1.5 py-0 flex-shrink-0`}>{typeInfo.label}</Badge>
                    {col.source_type === 'source_field' && col.source_field && (
                      <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">{getFieldLabel(col.source_field)}</span>
                    )}
                    {col.source_type === 'static_value' && col.static_value && (
                      <span className="text-[10px] text-green-400 truncate max-w-[80px]">= {col.static_value}</span>
                    )}
                    {hasTransform && col.source_type !== 'calculated' && (
                      <Badge className="bg-cyan-900/40 text-cyan-400 border-cyan-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                        <Wand2 className="w-2.5 h-2.5 mr-0.5" />{TRANSFORM_TYPE_INFO[col.transform_type as TransformType]?.label || col.transform_type}
                      </Badge>
                    )}
                    {col.is_required && <span className="text-[9px] text-red-400 flex-shrink-0 font-medium">REQ</span>}
                    {col.transform_config?.excludeFromExport && <EyeOff className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" title="Excluded from export" />}

                    {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                  </div>
                  {isExpanded && (
                    <ColumnEditorExpanded
                      col={col}
                      columns={columns}
                      onLocalChange={handleLocalChange}
                      onPersistChange={handleUpdateColumn}
                      onDelete={handleDeleteColumn}
                      saving={saving}
                      isLocked={isLocked}
                      marginRules={marginRules}
                      otherTemplateRulesGroups={otherTemplateRulesGroups}
                      rangeEndDate={rangeEndDate}
                      wide
                    />
                  )}
                </div>


              );
            })}
          </div>
          {/* Pop-out footer summary */}
          {columns.length > 0 && (
            <div className="flex-shrink-0 pt-2 border-t border-border/40 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
              <span>{columns.filter(c => c.is_enabled).length} enabled</span>
              <span className="text-border">|</span>
              <span>{columns.filter(c => c.source_type === 'source_field').length} mapped</span>
              <span className="text-border">|</span>
              <span>{columns.filter(c => c.source_type === 'static_value').length} static</span>
              <span className="text-border">|</span>
              <span>{columns.filter(c => c.source_type === 'blank').length} blank</span>
              <span className="text-border">|</span>
              <span>{columns.filter(c => c.source_type === 'calculated').length} calculated</span>
              {columns.filter(c => c.transform_type && c.transform_type !== 'none').length > 0 && (
                <>
                  <span className="text-border">|</span>
                  <span className="text-cyan-400">{columns.filter(c => c.transform_type && c.transform_type !== 'none').length} transformed</span>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateBuilder;
